import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {compose} from 'redux';
import * as Yup from 'yup';
import {ITenant} from '../../../../App';
import AppErrorMessage from '../../../../components/app-error-message';
import {
  AppForm,
  AppFormCheckboxField,
  AppFormDatePickerField,
  AppFormField,
  AppFormSubmitButton,
} from '../../../../components/forms';
import Header2 from '../../../../components/headers/header2';
import {Column, Row} from '../../../../components/layout';
import * as fonts from '../../../../util/fonts';
import {strings} from '../../../../util/strings';
import {getDateInServerFormat, has18Years, today} from '../../../../util/utility';
import './styles.scss';
import {useFormikContext} from 'formik';
import {deepStringObjectMerger} from '../../../../util/utility';
import {
  uriStartRegister,
  uriValidateMember,
} from '../../../../api/endpoints';
import {get, postWithCustomHeaders} from '../../../../api/api';
import {addToWizard} from '../../../../store/actions';

interface InsuranceValidationProps {
  onSubmit(values: any): any;
  setValid(value: boolean): void;
  nextStep(): void;
  valid: boolean;
  labels: typeof strings.pt.register;
  labelsLogin: typeof strings.pt.login;
  oneTrust: typeof strings.pt.oneTrust;
  messageLabels: typeof strings.pt.messages;
  globalStyles: any;
  tenant: ITenant;
  language: string;
  wizard: any;
  addToWizard?(value: any): void;
}

function InsuranceValidation({
  onSubmit,
  nextStep,
  labels,
  labelsLogin,
  oneTrust,
  messageLabels,
  globalStyles,
  tenant,
  language,
  wizard,
  addToWizard,
}: InsuranceValidationProps) {
  const formik = useFormikContext<any>();
  const [formikContext, setFormikContext] = useState<any>();
  const [submitting, setSubmitting] = useState(false);
  const [hoverErrorIcons, setHoverErrorIcons] = useState<any>({
    name: false,
    nif: false,
    dateOfBirth: false,
  });

  const labelsToUse = deepStringObjectMerger(strings.pt.register, labels);
  const labelsLoginToUse = deepStringObjectMerger(
    strings.pt.login,
    labelsLogin,
  );
  const oneTrustToUse = deepStringObjectMerger(strings.pt.oneTrust, oneTrust);
  const messageLabelsToUse = deepStringObjectMerger(
    strings.pt.messages,
    messageLabels,
  );

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required(messageLabelsToUse?.requiredField)
      .matches(/^(\S+\s*){1,10}$/, labelsToUse.register_nameValidationError),
    nif: Yup.string()
      .required(messageLabelsToUse?.requiredField)
      .matches(
        new RegExp(tenant?.vatNumberValidator),
        messageLabelsToUse?.invalidFormat,
      ),
    dateOfBirth: Yup.date()
      .required(messageLabelsToUse?.requiredField)
      .nullable()
      .max(today(), messageLabelsToUse?.invalidDate),
    acceptedTermsAndConditions: Yup.boolean()
      .required()
      .oneOf([true], messageLabelsToUse?.requiredField),
    acceptedDataTreatment: Yup.boolean()
      .required()
      .oneOf([true], messageLabelsToUse?.requiredField),
  });

  const defineOnMouseFunctions = (
    e: React.MouseEvent,
    fieldNamePar: string,
    dateInput?: boolean,
  ) => {
    var elem: HTMLElement = e.target as HTMLElement;
    if (!dateInput) {
      var icon: HTMLElement = elem.nextSibling as HTMLElement;
      defineIconFunctions(icon, fieldNamePar);
    }

    if (
      dateInput &&
      elem.tagName.includes('svg') &&
      elem.className['baseVal' as any].includes('error')
    ) {
      var icon: HTMLElement = elem as HTMLElement;
      defineIconFunctions(icon, fieldNamePar);
    }
  };

  const defineIconFunctions = (icon: HTMLElement, fieldNamePar: any) => {
    if (icon) {
      icon.onmouseover = function () {
        setHoverErrorIcons({...hoverErrorIcons, [fieldNamePar]: true});
      };
      icon.onmouseout = function () {
        setHoverErrorIcons({...hoverErrorIcons, [fieldNamePar]: false});
      };
    }
  };

  useEffect(() => {
    if (formikContext && (formikContext?.dirty as any)) {
      (formikContext as any)?.validateForm((formikContext as any)?.values);
    }
  }, [formikContext]);

  const history = useHistory();

  return (
    <Column className="insurance-validation-container">
      <Header2 className="insurance-validation-step-titile"></Header2>
      <Header2 style={{...fonts.ML, ...fonts.Regular}}>
        {labelsToUse?.register_pageSubTitle}
      </Header2>
      <AppForm
        className="insurance-validation-form-container"
        initialValues={{
          name: '',
          nif: '',
          dateOfBirth: null,
          acceptedTermsAndConditions: false,
          acceptedDataTreatment: false,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values: any, {validateForm}: any) => {
          const errors = await validateForm(values);
          if (Object.keys(errors).length > 0) {
            return;
          }

          if (!has18Years(values.dateOfBirth)) {
            history.push('/utilizador-menor');
            return;
          }

          setSubmitting(true);
          const featureName = 'InsuranceValidation';

          try {
            let formattedDate = '';
            if (values.dateOfBirth) {
              try {
                formattedDate = getDateInServerFormat(values.dateOfBirth);
              } catch (dateError) {
              }
            }

            const body = {
              nif: values.nif,
              dob: formattedDate,
              fullName: values.name,
              language: language || tenant?.mainLanguageAlias || 'PT',
            };

            const customHeaders = {
              headers: {
                'Content-Type': 'application/json',
                'Accept-Language': language || tenant?.mainLanguageAlias || 'PT',
                advSource: 'WebPortal',
                advBu:
                  wizard?.dataValidation?.currentUser?.contacts?.buId ||
                  tenant?.buDefaultAlias,
                advMemId: wizard?.insuranceValidation?.memId || '',
                feature: featureName,
                X_MYADV_API_KEY: process.env.REACT_APP_DEFAULT_TENANT_ALIAS,
              },
            };

            const registerRes: any = await postWithCustomHeaders(
              uriStartRegister(),
              JSON.stringify(body),
              customHeaders,
            );

            const isRegistered = !!(
              registerRes?.isRegisteredInWso2 ||
              registerRes?.data?.isRegisteredInWso2
            );

            if (isRegistered) {
              history.push('/utilizador-existente');
              return;
            }

            const sessionId = registerRes?.sessionId || registerRes?.data?.sessionId;

            if (!sessionId) {
              throw new Error('SessionId não recebido do servidor.');
            }

            await get(uriValidateMember(sessionId), false, 0, featureName);

            if (addToWizard) {
              addToWizard({
                insuranceValidation: {...values},
                dataValidation: {
                  ...wizard?.dataValidation,
                  currentUser: {
                    ...wizard?.dataValidation?.currentUser,
                    sessionId: sessionId,
                  },
                },
              });
            }

            onSubmit(values);
            nextStep();
          } catch (error: any) {
            const status = error?.status ?? error?.response?.status;
            if (status === 409) {
              history.push('/utilizador-existente');
              return;
            }
            history.push('/registo-bloqueado');
          } finally {
            setSubmitting(false);
          }
        }}>
        <Row className="mb-0" style={{position: 'relative'}}>
          <AppFormField
            showErrorMessage
            name="name"
            placeholder={labelsToUse?.register_namePlaceholder}
            handleFormikContext={() => {
              setFormikContext(formik);
            }}
            onMouseOver={(e) => {
              defineOnMouseFunctions(e, 'name');
            }}
            onMouseOut={() => {
              setHoverErrorIcons({...hoverErrorIcons, ['name']: false});
            }}
          />
        </Row>
        <Row className="insurance-validation-nif-row">
          <Column
            className="insurance-validation-nif-column"
            style={{position: 'relative'}}>
            <AppFormField
              showErrorMessage
              name="nif"
              placeholder={labelsToUse?.register_nifPlaceholder}
              onMouseOver={(e) => {
                defineOnMouseFunctions(e, 'nif');
              }}
              onMouseOut={() => {
                setHoverErrorIcons({...hoverErrorIcons, ['nif']: false});
              }}
            />
          </Column>
          <Column
            className="insurance-validation-dob-column"
            style={{position: 'relative'}}
            onMouseOver={(e) => {
              defineOnMouseFunctions(e, 'dateOfBirth', true);
            }}
            onMouseOut={() => {
              setHoverErrorIcons({
                ...hoverErrorIcons,
                ['dateOfBirth']: false,
              });
            }}>
            <AppFormDatePickerField
              showErrorMessage
              name="dateOfBirth"
              placeholder={labelsToUse?.register_birthdayPlaceholder}
              maxDate={new Date()}
              handleFormikContext={() => setFormikContext(formik)}
              onMouseOut={() => {
                setHoverErrorIcons({
                  ...hoverErrorIcons,
                  ['dateOfBirth']: false,
                });
              }}
            />
          </Column>
        </Row>
        <Row>
          <Column className="insurance-validation-checkboxes">
            <AppFormCheckboxField
              showErrorMessage
              purposeId={oneTrustToUse?.purposeTermsAndConditions}
              errorStyle={{marginLeft: '0'}}
              name="acceptedTermsAndConditions"
              label={
                <label
                  className="align-left-label"
                  style={{textAlign: 'start', ...fonts.SM, ...fonts.Medium}}>
                  {labelsToUse?.register_termsAndConditionsInit}{' '}
                  <a
                    className="insurance-validation-anchors"
                    style={{color: globalStyles?.primaryColor}}
                    href={labelsLoginToUse?.login_acceptConditionsUrl}
                    target="_blank">
                    {labelsToUse?.register_termsAndConditionsLink}
                  </a>
                </label>
              }
              labelClass="checkboxes-label"
            />
            <AppFormCheckboxField
              showErrorMessage
              purposeId={oneTrustToUse?.purposePolicyDataTreatment}
              name="acceptedDataTreatment"
              errorStyle={{marginLeft: '0'}}
              label={
                <label
                  className="align-left-label"
                  style={{...fonts.SM, ...fonts.Medium}}>
                  {labelsToUse?.register_privacyInit}{' '}
                  <a
                    className="insurance-validation-anchors"
                    style={{color: globalStyles?.primaryColor}}
                    href={labelsLoginToUse?.login_acceptPrivacyPolicyUrl}
                    target="_blank">
                    {labelsToUse?.register_privacyLink}
                  </a>
                </label>
              }
              labelClass="checkboxes-label"
            />
          </Column>
        </Row>
        <Row className="insurance-validation-continue-button-row">
          <AppFormSubmitButton
            disabled={submitting}
            title={labelsLoginToUse?.login_forgotPassword_continuar}
            className="stepper-continue-button"
          />
        </Row>
      </AppForm>
    </Column>
  );
}

const mapStateToProps = ({stringsDefault, globalStyles, tenant, language, wizard}: any) => ({
  labels: stringsDefault.value.pt.register,
  labelsLogin: stringsDefault.value.pt.login,
  oneTrust: stringsDefault.value.pt.oneTrust,
  messageLabels: stringsDefault.value.pt.messages,
  globalStyles: globalStyles.value,
  tenant: tenant.value,
  language: language.value,
  wizard: wizard.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  addToWizard: (value: any) => dispatch(addToWizard(value)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps)(InsuranceValidation));
